<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Reusable send-window editor for follow-up scheduling, used by both the
// follow-up automation form and the pipeline follow-up rule form. Emits the
// jsonb shape consumed by FollowUps::SendWindowResolver:
//   { enabled, days: [0-6], start: "HH:MM", end: "HH:MM" }  (0=Sun..6=Sat)
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Week displayed Monday-first; value follows Ruby Date#wday (0=Sun..6=Sat).
// Labels are resolved with static i18n keys (not a dynamic key) so the
// vue-i18n linter can verify each translation exists.
const WEEKDAYS = computed(() => [
  { value: 1, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.MON') },
  { value: 2, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.TUE') },
  { value: 3, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.WED') },
  { value: 4, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.THU') },
  { value: 5, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.FRI') },
  { value: 6, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.SAT') },
  { value: 0, label: t('FOLLOW_UP_SEND_WINDOW.WEEKDAYS.SUN') },
]);

const DEFAULT_WINDOW = {
  enabled: true,
  days: [1, 2, 3, 4, 5],
  start: '08:00',
  end: '18:00',
};

const sendWindow = computed(() => props.modelValue || {});
const isEnabled = computed(() => sendWindow.value.enabled === true);
const days = computed(() => sendWindow.value.days || []);
const startTime = computed(() => sendWindow.value.start || '08:00');
const endTime = computed(() => sendWindow.value.end || '18:00');

const update = patch => {
  emit('update:modelValue', { ...sendWindow.value, ...patch });
};

const onToggleEnabled = event => {
  if (!event.target.checked) {
    update({ enabled: false });
    return;
  }
  // Prefill a sensible default the first time the window is turned on.
  const hasConfig =
    days.value.length && sendWindow.value.start && sendWindow.value.end;
  emit(
    'update:modelValue',
    hasConfig ? { ...sendWindow.value, enabled: true } : { ...DEFAULT_WINDOW }
  );
};

const toggleDay = value => {
  const selected = new Set(days.value);
  if (selected.has(value)) {
    selected.delete(value);
  } else {
    selected.add(value);
  }
  update({ days: [...selected].sort((a, b) => a - b) });
};
</script>

<template>
  <div>
    <label class="flex items-center gap-2.5 cursor-pointer">
      <input
        :checked="isEnabled"
        type="checkbox"
        class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand focus:ring-offset-0"
        @change="onToggleEnabled"
      />
      <span class="text-[11px] font-medium text-n-slate-12">
        {{ $t('FOLLOW_UP_SEND_WINDOW.ENABLE') }}
      </span>
    </label>

    <div v-if="isEnabled" class="mt-3 space-y-3 pl-6">
      <div>
        <span class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
          {{ $t('FOLLOW_UP_SEND_WINDOW.DAYS') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="day in WEEKDAYS"
            :key="day.value"
            type="button"
            class="px-2.5 h-7 rounded-lg text-[11px] font-medium border transition-colors"
            :class="
              days.includes(day.value)
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-background text-n-slate-11 border-n-weak hover:bg-n-alpha-1'
            "
            @click="toggleDay(day.value)"
          >
            {{ day.label }}
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <div class="flex-1">
          <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
            {{ $t('FOLLOW_UP_SEND_WINDOW.START') }}
          </label>
          <input
            :value="startTime"
            type="time"
            class="w-full h-8 px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12"
            @input="update({ start: $event.target.value })"
          />
        </div>
        <div class="flex-1">
          <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
            {{ $t('FOLLOW_UP_SEND_WINDOW.END') }}
          </label>
          <input
            :value="endTime"
            type="time"
            class="w-full h-8 px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12"
            @input="update({ end: $event.target.value })"
          />
        </div>
      </div>

      <p class="text-[11px] text-n-slate-10">
        {{ $t('FOLLOW_UP_SEND_WINDOW.HINT') }}
      </p>
    </div>
  </div>
</template>
